import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { useEngineDomains } from '@/features/domains/hooks'
import type { MaturityLevel } from '@/features/maturity-levels/types'
import { useResults } from '@/features/results/hooks'
import { useAssessmentProgress } from '@/features/assessments/hooks'
import { Assessment } from '@/features/assessments/types'

interface DomainProgress {
  domain_id: string
  domain_name: string
  total_questions: number
  answered_questions: number
  average_score: number
  completion_percentage: number
}

interface QuestionnaireSummaryProps {
  assessment: Assessment
  answers: Record<string, number>
  onStartQuestionnaire: () => void
  onViewResults: () => void
  onViewAnswers?: () => void
}

export function QuestionnaireSummary({
  assessment,
  answers,
  onStartQuestionnaire,
  onViewResults,
  onViewAnswers
}: QuestionnaireSummaryProps) {
  const { data: maturityLevelsData } = useEngineMaturityLevels()
  const levels: MaturityLevel[] = maturityLevelsData?.data ?? maturityLevelsData ?? []

  // Load domains to get per-domain pass_threshold
  const { data: domainsData } = useEngineDomains()
  const domainsList = Array.isArray(domainsData) ? domainsData : domainsData?.data || []
  const domainThresholdMap: Record<string, number> = {}
  for (const d of domainsList) {
    if (d.name) domainThresholdMap[d.name.toLowerCase()] = d.pass_threshold ?? 0.70
    if (d.id) domainThresholdMap[d.id] = d.pass_threshold ?? 0.70
  }

  // Load progress from backend API
  const { data: progressData, isLoading: progressLoading } = useAssessmentProgress(assessment.id)

  // Load backend results via hook (only if assessment has answers)
  const hasAnswers = assessment?.id && Object.keys(answers).length > 0
  const { data: backendResultsData } = useResults(hasAnswers ? assessment.id : '')
  // overall_score is an object { domain_score, maturity_level, ... } — extract the number
  const backendScoreObj = backendResultsData?.overall_score ?? null
  const backendScore = typeof backendScoreObj === 'object' && backendScoreObj !== null
    ? backendScoreObj.domain_score ?? null
    : backendScoreObj

  // Extract progress data from API response
  const totalQuestions = progressData?.total_questions ?? 0
  const answeredQuestions = progressData?.total_answered ?? 0
  const progress = progressData?.overall_completion ?? 0
  const overallAvgScore = progressData?.overall_average_score ?? 0
  const domains: DomainProgress[] = progressData?.domains ?? []

  const getMaturityLevel = (score: number) => {
    // score is 0-1 range; find the highest level whose threshold is <= score
    const sorted = [...levels].sort((a, b) => b.level - a.level)
    // Map level thresholds to 0-1 scale: L5>=0.90, L4>=0.70, L3>=0.50, L2>=0.30, L1>=0
    const thresholds: Record<number, number> = { 5: 0.90, 4: 0.70, 3: 0.50, 2: 0.30, 1: 0 }
    for (const lvl of sorted) {
      const threshold = thresholds[lvl.level] ?? 0
      if (score >= threshold) return lvl
    }
    return sorted[sorted.length - 1] ?? { level: 0, name: '—', description: '', is_active: true }
  }

  // Use backend-calculated score (0-1) if available, otherwise normalize progress avg (0-5) to 0-1
  const overallScore = Number(backendScore !== null ? backendScore : (overallAvgScore / 5)) || 0
  const maturityLevel = getMaturityLevel(overallScore)
  const isAssessmentCompleted = assessment.status === 'completed'

  return (
    <div className="space-y-6">
      {/* Assessment Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Assessment Summary</CardTitle>
          <CardDescription className="flex items-center gap-2">
            <span>{assessment.name} - {assessment.status ? assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1) : 'Unknown Status'}</span>
            {assessment.assessment_scope === 'level' && assessment.scope_level && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                Scope: Level {assessment.scope_level}
              </span>
            )}
            {assessment.assessment_scope === 'industry' && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-700/10">
                Scope: Industry Benchmark
              </span>
            )}
            {(!assessment.assessment_scope || assessment.assessment_scope === 'complete') && (
              <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                Complete Assessment
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Progress</div>
              <div className="text-2xl font-bold">{Math.round(progress)}%</div>
              <Progress value={progress} className="mt-2" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Questions Answered</div>
              <div className="text-2xl font-bold">
                {progressLoading ? '...' : `${answeredQuestions}/${totalQuestions}`}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Current Score</div>
              <div className="text-2xl font-bold">{(overallScore * 100).toFixed(0)}%</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Maturity Level */}
      {overallScore > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Current Maturity Level</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <div className="text-4xl font-bold text-primary">
                {maturityLevel.level}
              </div>
              <div>
                <div className="text-xl font-semibold">{maturityLevel.name}</div>
                <div className="text-muted-foreground">{maturityLevel.description}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domain Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Domain Progress</CardTitle>
          <CardDescription>Progress for each IT maturity domain</CardDescription>
        </CardHeader>
        <CardContent>
          {progressLoading ? (
            <div className="text-center py-4 text-muted-foreground">Loading domain progress...</div>
          ) : (
            <div className="space-y-4">
              {domains.map((domain) => {
                const isDomainComplete = domain.answered_questions === domain.total_questions && domain.total_questions > 0

                return (
                  <div key={domain.domain_id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{domain.domain_name}</span>
                        {isDomainComplete && (
                          <span className="text-green-600 text-sm">✓ Complete</span>
                        )}
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                          Pass Rate: {Math.round((domainThresholdMap[domain.domain_name?.toLowerCase()] ?? domainThresholdMap[domain.domain_id] ?? 0.70) * 100)}%
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        {domain.answered_questions > 0 && (
                          <span className="text-sm text-muted-foreground">
                            Avg: {(domain.average_score / 5 * 100).toFixed(0)}%
                          </span>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {domain.answered_questions}/{domain.total_questions} questions
                        </span>
                      </div>
                    </div>
                    <Progress value={domain.completion_percentage} className="h-2" />
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {progress < 100 && (
                <Button
                  variant="outline"
                  onClick={onStartQuestionnaire}
                >
                  {answeredQuestions === 0 ? 'Start Assessment' : 'Continue Assessment'}
                </Button>
              )}

              {isAssessmentCompleted && onViewAnswers && (
                <Button variant="outline" onClick={onViewAnswers}>
                  View Answers
                </Button>
              )}
            </div>

            {isAssessmentCompleted && (
              <Button onClick={onViewResults}>
                View Results
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
